import React, { useRef, useContext, useEffect, useState, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { ellipsis, defaultTransition } from '@peloton/styles';
import { isDefined } from '@peloton/types';
import { white, gray4, slate3, slate1 } from '@engage/colors';
import { spaces } from '@engage/styles';
import { label16 } from '@engage/typography';
import { useClassSearch, useCta, ClassSearchCopy } from '@members/copy';
import { ClassSearch } from '@members/copy/models.generated/ClassSearch';
import { Cta } from '@members/copy/models.generated/Cta';
import CancelIcon from '@members/icons/CancelIcon';
import LoadingSpinner from '@members/loading/LoadingSpinner';
import { SearchPageContext, input, useDebouncedSavedSearch } from './context';
import { useIsSearching } from './context/useIsSearching';
import { useSearchInput } from './context/useSearchInput';
import { SearchSparkle } from './SearchSparkle';

enum InputState {
  Empty = 0,
  Searching,
  Filled,
}

export const SEARCH_TERMS = [
  ClassSearch.CoreStrength,
  ClassSearch.Pilates,
  ClassSearch.TaylorSwiftYoga,
  ClassSearch.BroadwayRide,
];

export const SearchInput: React.FC<React.PropsWithChildren<unknown>> = () => {
  const inputRef = useRef<HTMLInputElement>(null);
  const {
    dispatch,
    pageState: { query, status },
  } = useContext(SearchPageContext);

  const [localQuery, setLocalQuery] = useState(query);

  const isSearching = useIsSearching();

  const searchInputLabel = useClassSearch(ClassSearch.SearchInputLabel);

  const inputState = useMemo(() => {
    if ((localQuery?.length ?? 0) === 0) {
      return InputState.Empty;
    } else if (isSearching) {
      return InputState.Searching;
    } else {
      return InputState.Filled;
    }
  }, [localQuery, isSearching]);

  const searchInput = useSearchInput();
  const debouncedSavedSearch = useDebouncedSavedSearch();

  useEffect(() => {
    if (isDefined(localQuery)) {
      searchInput(localQuery);
      debouncedSavedSearch(localQuery);
    }
  }, [localQuery, searchInput]);

  useEffect(() => {
    if (localQuery !== query) {
      setLocalQuery(query);
    }
  }, [query]);

  // Automatically focus on text input.
  useEffect(() => {
    if (status === 'initial' && (query ?? '').length === 0) {
      inputRef.current?.focus();
    }
  }, [status, query]);

  return (
    <InputContainer data-test-id="searchInputContainer" state={inputState}>
      <LoadingIcon isLoading={true} size={12} state={inputState} />
      <Input
        autoComplete="off"
        name="searchTerm"
        data-test-id="searchInput"
        type="text"
        value={localQuery}
        onChange={event => setLocalQuery(event.currentTarget.value)}
        aria-label={searchInputLabel}
        ref={inputRef}
      />
      <StyledLabel
        data-test-id="searchPlaceholder"
        state={inputState}
        htmlFor={searchInputLabel}
      >
        <ClassSearchCopy id={ClassSearch.LinkTitle} />{' '}
        <SearchSparkle searchTerms={SEARCH_TERMS} />
      </StyledLabel>
      <ClearButton
        onClick={() => {
          dispatch(input(''));
        }}
        aria-label={useCta(Cta.InputClear)}
      >
        <CancelIcon data-test-id="clearIcon" />
      </ClearButton>
    </InputContainer>
  );
};

const Input = styled.input`
  ${label16}
  background-color: ${white};
  outline: none;
  border: none;
  width: 100%;
  height: ${spaces.xHuge}px;
  color: ${slate3};
  &::-ms-clear {
    // IE clear button
    display: none;
    width: 0;
    height: 0;
  }
`;

const visibleStyle = css`
  opacity: 1;
`;

const hiddenStyle = css`
  opacity: 0;
`;

const StyledLabel = styled.label<{ state: InputState }>`
  ${label16}
  color: ${slate1};
  position: absolute;
  pointer-events: none;
  left: 0;
  top: 0;
  ${defaultTransition('all', 200)}
  height: 100%;
  width: 100%;
  text-align: left;
  align-items: center;
  display: flex;

  ${props => (props.state === InputState.Empty ? visibleStyle : hiddenStyle)}

  & > div {
    ${ellipsis}
  }
`;

const LoadingIcon = styled(LoadingSpinner)<{ state: InputState }>`
  ${defaultTransition('all', 200)}
  fill: ${slate3};
  height: ${spaces.medium}px;
  position: absolute;
  right: 0px;
  top: 17px;
  width: ${spaces.medium}px;
  ${props => (props.state === InputState.Searching ? visibleStyle : hiddenStyle)}
`;

const ClearButton = styled.button`
  ${defaultTransition('opacity')}
  cursor: pointer;
  fill: ${gray4};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  padding: 12px;
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);

  svg {
    width: ${spaces.medium}px;
    height: ${spaces.medium}px;
  }
`;

const InputContainer = styled.div<{ state: InputState }>`
  position: relative;
  flex: 1;
  height: ${spaces.xHuge}px;
  ${label16};

  ${ClearButton} {
    opacity: ${props => (props.state === InputState.Filled ? '1' : '0')};
    visibility: ${props => (props.state === InputState.Filled ? 'visible' : 'hidden')};
  }
`;
